/**
 * PropertiesPanel.tsx
 * Panneau de propriétés de l'objet sélectionné
 * Remplace PropertiesPanelService.ts par une approche déclarative React
 */
import React, { useCallback, useState } from 'react';
import { useAppStore } from '../../../services/StateService';
import type { ShapeData } from '../../../types/ShapeTypes';
import { LayersTabContent } from './LayersTabContent';
import { LayoutsTabContent } from './LayoutsTabContent';
import { VariablesManagerModal } from '../VariablesManagerModal';
import { TextStylesPanel } from '../TextStylesPanel';
import { useAppCoordinatorOptional } from '../../../hooks/useAppCoordinator';
import type { TextStyle } from '../../../types';
import { ImageFilterManager, type ImageFilter } from '../../../ImageFilterManager';
import { ImageCropModal } from '../ImageCrop/ImageCropModal';
import { blobToDataURL, type CropArea } from '../../../services/ImageCropService';
import Konva from 'konva';
import { CollapsibleSection } from '../common/CollapsibleSection';
import { SliderInput } from '../common/SliderInput';
import { EventBus } from '../../../services/EventBus';
import { logger } from '../../../utils/ProfessionalLogger';
import { Input } from '../../../ui';
import { canvasStageRefService } from '../../../hooks/useCanvasStageRef';

/**
 * Input numérique contrôlé
 */
const NumericInput: React.FC<{
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
}> = ({ label, value, onChange, min, max, step = 1, unit }) => (
  <Input
    type="number"
    label={label}
    value={Math.round(value * 100) / 100}
    onChange={e => onChange(parseFloat(e.target.value) || 0)}
    min={min}
    max={max}
    step={step}
    className={unit ? 'pr-12' : ''}
    rightIcon={unit ? <span className="text-xs text-gray-500">{unit}</span> : undefined}
    fullWidth
  />
);

/**
 * Input couleur
 */
const ColorInput: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
}> = ({ label, value, onChange }) => (
  <div className="control-group">
    <label>{label}</label>
    <div className="color-controls">
      <input
        type="color"
        className="color-picker"
        value={value}
        onChange={e => onChange(e.target.value)}
      />
      <input
        type="text"
        className="input"
        value={value.toUpperCase()}
        onChange={e => onChange(e.target.value)}
        pattern="^#[0-9A-Fa-f]{6}$"
      />
    </div>
  </div>
);

/**
 * Propriétés du document (quand rien n'est sélectionné)
 */
const _DocumentProperties: React.FC = () => {
  const templateWidth = useAppStore(state => state.canvas.templateWidth);
  const templateHeight = useAppStore(state => state.canvas.templateHeight);
  const backgroundColor = useAppStore(state => state.canvas.backgroundColor);
  const setTemplateDimensions = useAppStore(state => state.setTemplateDimensions);
  const setBackgroundColor = useAppStore(state => state.setBackgroundColor);

  return (
    <>
      <div className="sidebar-section">
        <h3 className="section-title">Document</h3>
        <div className="properties-content">
          <NumericInput
            label="Largeur"
            value={templateWidth}
            onChange={w => setTemplateDimensions(w, templateHeight)}
            min={100}
            max={5000}
            unit="px"
          />
          <NumericInput
            label="Hauteur"
            value={templateHeight}
            onChange={h => setTemplateDimensions(templateWidth, h)}
            min={100}
            max={5000}
            unit="px"
          />
          <ColorInput
            label="Couleur de fond"
            value={backgroundColor}
            onChange={setBackgroundColor}
          />
        </div>
      </div>
    </>
  );
};

/**
 * PHASE 3.3: Contrôles des filtres d'image
 */
const ImageFiltersControls: React.FC<{
  shape: ShapeData;
  onUpdate: (updates: Partial<ShapeData>) => void;
}> = ({ shape, onUpdate }) => {
  const appCoordinator = useAppCoordinatorOptional();
  const [filters, setFilters] = React.useState<ImageFilter>({
    brightness: 0,
    contrast: 0,
    blur: 0,
    saturation: 0,
  });
  const [showCropModal, setShowCropModal] = React.useState(false);
  const [imageSrc, setImageSrc] = React.useState<string | null>(null);

  // Charger les filtres actuels depuis Konva
  React.useEffect(() => {
    const stage = canvasStageRefService.getStage();
    if (stage) {
      const node = stage.findOne(`#${shape.id}`);
      if (node) {
        const currentFilters = ImageFilterManager.getFilters(node);
        setFilters(currentFilters);
      }
    }
  }, [shape.id]);

  const applyFilter = React.useCallback(
    (filterType: keyof ImageFilter, value: number) => {
      const newFilters = { ...filters, [filterType]: value };
      setFilters(newFilters);

      // Appliquer le filtre en temps réel sur le nœud Konva
      const stage = canvasStageRefService.getStage();
      if (stage) {
        const node = stage.findOne(`#${shape.id}`);
        if (node) {
          ImageFilterManager.applyFilters(node, { [filterType]: value });
          node.getLayer()?.batchDraw();
        }
      }
    },
    [filters, shape.id, appCoordinator]
  );

  const resetFilters = React.useCallback(() => {
    const defaultFilters = { brightness: 0, contrast: 0, blur: 0, saturation: 0 };
    setFilters(defaultFilters);

    const stage = canvasStageRefService.getStage();
    if (stage) {
      const node = stage.findOne(`#${shape.id}`);
      if (node) {
        ImageFilterManager.resetFilters(node);
        node.getLayer()?.batchDraw();
      }
    }
  }, [shape.id, appCoordinator]);

  const handleCropClick = React.useCallback(() => {
    if (!shape.src) {
      return;
    }

    const stage = canvasStageRefService.getStage();
    if (!stage) {
      return;
    }

    const node = stage.findOne(`#${shape.id}`) as Konva.Image;
    if (!node) {
      return;
    }

    // Récupérer l'image source
    const image = node.image();
    if (!image) {
      return;
    }

    // Créer un canvas temporaire pour obtenir l'URL de l'image
    // P3 : CanvasImageSource est une union (Image | ImageBitmap | …) ; on cast en HTMLImageElement.
    const img = image as HTMLImageElement;
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(image, 0, 0);
      setImageSrc(canvas.toDataURL('image/png'));
      setShowCropModal(true);
    }
  }, [shape.id, shape.src]);

  const handleCropComplete = React.useCallback(
    async (croppedBlob: Blob, cropData: CropArea) => {
      try {
        // Convertir le Blob en URL de données
        const dataUrl = await blobToDataURL(croppedBlob);

        // Appliquer le recadrage au nœud Konva
        const stage = canvasStageRefService.getStage();
        if (stage) {
          const node = stage.findOne(`#${shape.id}`) as Konva.Image;
          if (node) {
            // Charger la nouvelle image recadrée
            const image = new window.Image();
            image.crossOrigin = 'anonymous';
            image.onload = () => {
              node.image(image);
              node.width(cropData.width);
              node.height(cropData.height);
              node.getLayer()?.batchDraw();

              // Mettre à jour les données de la forme
              onUpdate({
                src: dataUrl,
                width: cropData.width,
                height: cropData.height,
                cropData,
              });
            };
            image.src = dataUrl;
          }
        }
      } catch (error) {
        logger.error("Erreur lors de l'application du recadrage", { error }, 'PropertiesPanel');
      }
    },
    [shape.id, appCoordinator, onUpdate]
  );

  return (
    <>
      <NumericInput
        label="Luminosité"
        value={filters.brightness}
        onChange={value => applyFilter('brightness', value)}
        min={-100}
        max={100}
        step={1}
        unit="%"
      />
      <NumericInput
        label="Contraste"
        value={filters.contrast}
        onChange={value => applyFilter('contrast', value)}
        min={-100}
        max={100}
        step={1}
        unit="%"
      />
      <NumericInput
        label="Flou"
        value={filters.blur}
        onChange={value => applyFilter('blur', value)}
        min={0}
        max={50}
        step={1}
        unit="px"
      />
      <NumericInput
        label="Saturation"
        value={filters.saturation}
        onChange={value => applyFilter('saturation', value)}
        min={-100}
        max={100}
        step={1}
        unit="%"
      />
      <div
        style={{
          display: 'flex',
          gap: '8px',
          marginTop: 'var(--spacing-md)',
        }}
      >
        <button
          onClick={handleCropClick}
          style={{
            flex: 1,
            padding: '8px 16px',
            background: 'var(--color-primary)',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: '500',
          }}
        >
          Recadrer
        </button>
        <button
          onClick={resetFilters}
          style={{
            flex: 1,
            padding: '8px 16px',
            background: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border)',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
          }}
        >
          Réinitialiser
        </button>
      </div>
      {showCropModal && imageSrc && (
        <ImageCropModal
          isOpen={showCropModal}
          imageSrc={imageSrc}
          onClose={() => {
            setShowCropModal(false);
            setImageSrc(null);
          }}
          onCropComplete={handleCropComplete}
        />
      )}
    </>
  );
};

/**
 * Propriétés d'une forme
 */
const _ShapeProperties: React.FC<{
  shape: ShapeData;
  onShowVariablesModal?: () => void;
  onShowTextStylesPanel?: () => void;
}> = ({ shape, onShowVariablesModal, onShowTextStylesPanel }) => {
  const updateShape = useAppStore(state => state.updateShape);
  const deleteShape = useAppStore(state => state.deleteShape);
  const duplicateShape = useAppStore(state => state.duplicateShape);

  // CORRIGÉ: Debounce pour éviter les boucles infinies et améliorer les performances
  // Les mises à jour fréquentes (comme le texte) sont debounced, les autres sont immédiates
  const debounceRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleUpdate = useCallback(
    (updates: Partial<ShapeData>) => {
      // Pour le texte, utiliser un debounce pour éviter trop d'updates
      if ('text' in updates) {
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
          updateShape(shape.id, updates);
        }, 300); // 300ms de debounce pour le texte
      } else {
        // Pour les autres propriétés, mise à jour immédiate
        updateShape(shape.id, updates);
      }
    },
    [shape.id, updateShape]
  );

  // Cleanup du debounce au démontage
  React.useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleDelete = useCallback(() => {
    deleteShape(shape.id);
  }, [shape.id, deleteShape]);

  const handleDuplicate = useCallback(() => {
    duplicateShape(shape.id);
  }, [shape.id, duplicateShape]);

  // CORRIGÉ: Handler pour changer de calque (utilise AppCoordinator)
  const appCoordinator = useAppCoordinatorOptional();
  const handleChangeLayer = useCallback(
    (targetLayer: 'background' | 'photos' | 'overlay') => {
      // P3 : `changeShapeLayer` n'est pas exposé par ReactFirstAppCoordinator
      // actuellement ; on met à jour directement le store (fallback robuste).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const fn = (appCoordinator as any)?.changeShapeLayer;
      if (typeof fn === 'function') {
        fn.call(appCoordinator, shape.id, targetLayer);
      } else {
        updateShape(shape.id, { layer: targetLayer });
      }
    },
    [shape.id, updateShape, appCoordinator]
  );

  return (
    <>
      {/* UX: Section Transform avec CollapsibleSection et SliderInput */}
      <CollapsibleSection
        title="Transform"
        icon={
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
            <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
            <line x1="12" y1="22.08" x2="12" y2="12" />
          </svg>
        }
        defaultExpanded={true}
      >
        <div className="properties-content">
          <div className="properties-grid">
            <NumericInput label="X" value={shape.x} onChange={x => handleUpdate({ x })} unit="px" />
            <NumericInput label="Y" value={shape.y} onChange={y => handleUpdate({ y })} unit="px" />
          </div>
          <div className="properties-grid">
            <SliderInput
              label="Largeur"
              value={shape.width || 100}
              onChange={width => handleUpdate({ width })}
              min={1}
              max={2000}
              step={1}
              unit="px"
              showSlider={true}
              showInput={true}
            />
            <SliderInput
              label="Hauteur"
              value={shape.height || 100}
              onChange={height => handleUpdate({ height })}
              min={1}
              max={2000}
              step={1}
              unit="px"
              showSlider={true}
              showInput={true}
            />
          </div>
          <SliderInput
            label="Rotation"
            value={shape.rotation || 0}
            onChange={rotation => handleUpdate({ rotation })}
            min={-360}
            max={360}
            step={1}
            unit="°"
            showSlider={true}
            showInput={true}
          />
          <SliderInput
            label="Opacité"
            value={(shape.opacity ?? 1) * 100}
            onChange={opacity => handleUpdate({ opacity: opacity / 100 })}
            min={0}
            max={100}
            step={1}
            unit="%"
            showSlider={true}
            showInput={true}
            precision={0}
          />
        </div>
      </CollapsibleSection>

      {/* UX: Section Apparence avec CollapsibleSection */}
      <CollapsibleSection
        title="Apparence"
        icon={
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 2a15.3 15.3 0 0 0 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 0 4-10z" />
          </svg>
        }
        defaultExpanded={true}
      >
        <div className="properties-content">
          {shape.fill !== undefined && (
            <ColorInput
              label="Remplissage"
              value={shape.fill || '#000000'}
              onChange={fill => handleUpdate({ fill })}
            />
          )}
          {shape.stroke !== undefined && (
            <>
              <ColorInput
                label="Contour"
                value={shape.stroke || '#000000'}
                onChange={stroke => handleUpdate({ stroke })}
              />
              <SliderInput
                label="Épaisseur"
                value={shape.strokeWidth || 0}
                onChange={strokeWidth => handleUpdate({ strokeWidth })}
                min={0}
                max={50}
                step={1}
                unit="px"
                showSlider={true}
                showInput={true}
              />
            </>
          )}
          {/* Blend Mode */}
          <div style={{ marginTop: '12px' }}>
            <label
              style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: '500' }}
            >
              Mode de fusion
            </label>
            <select
              value={shape.blendMode || 'normal'}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onChange={e => handleUpdate({ blendMode: e.target.value as any })}
              aria-label="Mode de fusion de l'objet"
              style={{
                width: '100%',
                padding: '6px 8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '12px',
                background: 'white',
              }}
            >
              <option value="normal">Normal</option>
              <option value="multiply">Multiplier</option>
              <option value="screen">Écran</option>
              <option value="overlay">Superposition</option>
              <option value="darken">Assombrir</option>
              <option value="lighten">Éclaircir</option>
              <option value="color-dodge">Dodge couleur</option>
              <option value="color-burn">Brûlure couleur</option>
              <option value="hard-light">Lumière dure</option>
              <option value="soft-light">Lumière douce</option>
              <option value="difference">Différence</option>
              <option value="exclusion">Exclusion</option>
              <option value="hue">Teinte</option>
              <option value="saturation">Saturation</option>
              <option value="color">Couleur</option>
              <option value="luminosity">Luminosité</option>
            </select>
          </div>
        </div>
      </CollapsibleSection>

      {/* Section Texte (si type = text) */}
      {shape.type === 'text' && (
        <div className="sidebar-section">
          <h3 className="section-title">Texte</h3>
          <div className="properties-content">
            <div
              style={{
                padding: 'var(--spacing-md)',
                backgroundColor: 'var(--color-bg-secondary)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-border)',
                textAlign: 'center',
                marginBottom: 'var(--spacing-md)',
              }}
            >
              <p
                style={{
                  margin: 0,
                  color: 'var(--color-text-primary)',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                📝 Les propriétés principales du texte (contenu, taille, police, couleur, style,
                alignement) sont disponibles dans le panneau <strong>Texte</strong> à gauche.
              </p>
              <p
                style={{
                  margin: 'var(--spacing-xs) 0 var(--spacing-sm) 0',
                  color: 'var(--color-text-secondary)',
                  fontSize: 'var(--font-size-xs)',
                }}
              >
                Cliquez sur l'icône "Texte" dans la barre latérale pour accéder aux propriétés
                complètes.
              </p>
              <button
                onClick={() => {
                  // Émettre un événement pour ouvrir le TextDrawer
                  const eventBus = EventBus.getInstance();
                  eventBus.emit({
                    type: 'ui-action',
                    timestamp: Date.now(),
                    data: { drawerType: 'text' },
                  });
                }}
                className="btn btn-primary btn-sm"
                style={{
                  width: '100%',
                  marginTop: 'var(--spacing-xs)',
                }}
                title="Ouvrir le panneau Texte"
              >
                Ouvrir le panneau Texte
              </button>
            </div>

            {/* Section Fond du texte */}
            <div className="control-group">
              <label style={{ marginTop: '16px', marginBottom: '8px' }}>Fond du texte</label>
              <ColorInput
                label="Couleur de fond"
                value={shape.textBackgroundColor || '#ffffff'}
                onChange={textBackgroundColor => handleUpdate({ textBackgroundColor })}
              />
              <div className="properties-grid" style={{ marginTop: '8px' }}>
                <NumericInput
                  label="Padding"
                  value={shape.textBackgroundPadding || 0}
                  onChange={textBackgroundPadding => handleUpdate({ textBackgroundPadding })}
                  min={0}
                  max={50}
                  unit="px"
                />
                <NumericInput
                  label="Rayon"
                  value={shape.textBackgroundCornerRadius || 0}
                  onChange={textBackgroundCornerRadius =>
                    handleUpdate({ textBackgroundCornerRadius })
                  }
                  min={0}
                  max={50}
                  unit="px"
                />
              </div>
            </div>

            {/* Section Variables de texte */}
            <div className="control-group">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '16px',
                  marginBottom: '8px',
                }}
              >
                <label>Variables dynamiques</label>
                <div style={{ display: 'flex', gap: '6px' }}>
                  {/* PHASE 2.2: Bouton pour ouvrir VariablesManagerModal - RÉACTIVÉ */}
                  <button
                    onClick={() => {
                      onShowVariablesModal?.();
                    }}
                    style={{
                      padding: '4px 8px',
                      background: 'var(--color-primary)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '11px',
                    }}
                    title="Gérer les variables"
                  >
                    Gérer
                  </button>
                  {/* Bouton pour ouvrir TextStylesPanel - visible uniquement pour les textes */}
                  {shape.type === 'text' && onShowTextStylesPanel && (
                    <button
                      onClick={() => {
                        onShowTextStylesPanel();
                      }}
                      style={{
                        padding: '4px 8px',
                        background: 'var(--color-bg-secondary)',
                        color: 'var(--color-text-primary)',
                        border: '1px solid var(--color-border)',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '11px',
                      }}
                      title="Styles de texte"
                    >
                      Styles
                    </button>
                  )}
                </div>
              </div>
              <div
                style={{
                  fontSize: '12px',
                  color: 'var(--color-text-secondary)',
                  marginBottom: '8px',
                }}
              >
                Utilisez {'{{nom}}'} pour insérer des variables
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {Object.entries(shape.textVariables || {}).map(([key, value]) => (
                  <div key={key} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <input
                      type="text"
                      className="input"
                      value={key}
                      readOnly
                      style={{ flex: 1, fontSize: '12px' }}
                    />
                    <input
                      type="text"
                      className="input"
                      value={value}
                      onChange={e => {
                        const newVars = { ...shape.textVariables, [key]: e.target.value };
                        handleUpdate({ textVariables: newVars });
                      }}
                      style={{ flex: 2, fontSize: '12px' }}
                    />
                    <button
                      onClick={() => {
                        const newVars = { ...shape.textVariables };
                        delete newVars[key];
                        handleUpdate({ textVariables: newVars });
                      }}
                      style={{
                        padding: '4px 8px',
                        background: 'var(--color-danger)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '11px',
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => {
                    const newKey = `var${Object.keys(shape.textVariables || {}).length + 1}`;
                    const newVars = { ...shape.textVariables, [newKey]: '' };
                    handleUpdate({ textVariables: newVars });
                  }}
                  style={{
                    padding: '6px 12px',
                    background: 'var(--color-bg-secondary)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '500',
                  }}
                >
                  + Ajouter une variable
                </button>
              </div>
            </div>

            {/* Section Styles de texte */}
            <div className="control-group">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '16px',
                  marginBottom: '8px',
                }}
              >
                <label>Styles de texte</label>
                <button
                  onClick={() => onShowTextStylesPanel?.()}
                  style={{
                    padding: '4px 8px',
                    background: 'var(--color-primary)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '11px',
                  }}
                  title="Gérer les styles de texte"
                >
                  Gérer
                </button>
              </div>
            </div>
            <TextStylesSection shape={shape} onUpdate={handleUpdate} />
          </div>
        </div>
      )}

      {/* PHASE 3.3: Section Filtres d'image - Toujours visible */}
      <div className="sidebar-section">
        <h3 className="section-title">Filtres d'image</h3>
        <div className="properties-content">
          {shape.type === 'image' || shape.type === 'photoSpace' ? (
            <ImageFiltersControls shape={shape} onUpdate={handleUpdate} />
          ) : (
            <p
              style={{
                fontSize: '12px',
                color: 'var(--color-text-secondary)',
                textAlign: 'center',
                padding: 'var(--spacing-md)',
              }}
            >
              Sélectionnez une image pour appliquer des filtres
            </p>
          )}
        </div>
      </div>

      {/* Section Ombres et effets (pour tous les objets) */}
      {shape.type !== 'group' && (
        <div className="sidebar-section">
          <h3 className="section-title">Ombres et effets</h3>
          <div className="properties-content">
            <div className="control-group">
              <label>
                <input
                  type="checkbox"
                  checked={(shape.shadowBlur || 0) > 0}
                  onChange={e => {
                    if (e.target.checked) {
                      handleUpdate({
                        shadowBlur: shape.shadowBlur || 10,
                        shadowColor: shape.shadowColor || '#000000',
                        shadowOffsetX: shape.shadowOffsetX || 0,
                        shadowOffsetY: shape.shadowOffsetY || 0,
                      });
                    } else {
                      handleUpdate({ shadowBlur: 0 });
                    }
                  }}
                  style={{ marginRight: '8px' }}
                />
                Activer l'ombre
              </label>
            </div>

            {(shape.shadowBlur || 0) > 0 && (
              <>
                <ColorInput
                  label="Couleur de l'ombre"
                  value={shape.shadowColor || '#000000'}
                  onChange={shadowColor => handleUpdate({ shadowColor })}
                />
                <div className="properties-grid">
                  <NumericInput
                    label="Flou"
                    value={shape.shadowBlur || 10}
                    onChange={shadowBlur => handleUpdate({ shadowBlur })}
                    min={0}
                    max={50}
                    unit="px"
                  />
                  <NumericInput
                    label="Opacité"
                    value={
                      shape.shadowColor
                        ? (parseInt(shape.shadowColor.slice(-2) || '80', 16) / 255) * 100
                        : 50
                    }
                    onChange={opacity => {
                      const hex = shape.shadowColor?.slice(0, 7) || '#000000';
                      const alpha = Math.round((opacity / 100) * 255)
                        .toString(16)
                        .padStart(2, '0');
                      handleUpdate({ shadowColor: `${hex}${alpha}` });
                    }}
                    min={0}
                    max={100}
                    unit="%"
                  />
                </div>
                <div className="properties-grid">
                  <NumericInput
                    label="Décalage X"
                    value={shape.shadowOffsetX || 0}
                    onChange={shadowOffsetX => handleUpdate({ shadowOffsetX })}
                    min={-50}
                    max={50}
                    unit="px"
                  />
                  <NumericInput
                    label="Décalage Y"
                    value={shape.shadowOffsetY || 0}
                    onChange={shadowOffsetY => handleUpdate({ shadowOffsetY })}
                    min={-50}
                    max={50}
                    unit="px"
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Section Actions */}
      <div className="sidebar-section">
        <h3 className="section-title">Actions</h3>
        <div className="object-actions">
          <button className="btn btn-secondary btn-sm" onClick={handleDuplicate}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
            </svg>
            Dupliquer
          </button>
          <button className="btn btn-danger btn-sm" onClick={handleDelete}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
            </svg>
            Supprimer
          </button>
        </div>
        {/* CORRIGÉ: Boutons pour changer de calque (pour l'export photobooth) */}
        <div className="object-actions" style={{ marginTop: '8px' }}>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => handleChangeLayer('overlay')}
            title="Mettre au premier plan (overlay)"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M12 2L2 7l10 5 10-5-10-5z" />
              <path d="M2 17l10 5 10-5" />
              <path d="M2 12l10 5 10-5" />
            </svg>
            Premier plan
          </button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => handleChangeLayer('background')}
            title="Mettre à l'arrière-plan (background)"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M12 22L2 17l10-5 10 5-10 5z" />
              <path d="M2 7l10 5 10-5" />
              <path d="M2 12l10 5 10-5" />
            </svg>
            Arrière-plan
          </button>
        </div>
      </div>
    </>
  );
};

/**
 * Composant PropertiesPanel principal
 */
export function PropertiesPanel(): React.ReactElement {
  const _appCoordinator = useAppCoordinatorOptional();
  const shapes = useAppStore(state => state.shapes);
  // P0.2 FIX: Utiliser selection.primaryId au lieu de canvas.selectedNodeId
  const selectedNodeId = useAppStore(state => state.selection.primaryId);
  const [activeTab, setActiveTab] = React.useState<'layers' | 'layouts'>('layers');
  const [showVariablesModal, setShowVariablesModal] = useState(false);
  const [showTextStylesPanel, setShowTextStylesPanel] = useState(false);

  const selectedShape = shapes.find(s => s.id === selectedNodeId);

  return (
    <aside
      className="snapbooth-right-sidebar"
      id="rightSidebar"
      role="complementary"
      aria-label="Panneau de propriétés"
    >
      {/* Onglets */}
      <div className="sidebar-tabs">
        <button
          className={`sidebar-tab ${activeTab === 'layers' ? 'active' : ''}`}
          onClick={() => setActiveTab('layers')}
        >
          Calques
        </button>
        <button
          className={`sidebar-tab ${activeTab === 'layouts' ? 'active' : ''}`}
          onClick={() => setActiveTab('layouts')}
        >
          Layouts
        </button>
      </div>

      {/* Contenu des onglets */}
      <div className="sidebar-tab-content">
        {activeTab === 'layers' ? (
          <LayersTabContent
            selectedShape={selectedShape}
            onShowVariablesModal={() => setShowVariablesModal(true)}
            onShowTextStylesPanel={() => setShowTextStylesPanel(true)}
          />
        ) : (
          <LayoutsTabContent />
        )}
      </div>

      {/* Modal de gestion des variables */}
      <VariablesManagerModal
        isOpen={showVariablesModal}
        onClose={() => setShowVariablesModal(false)}
      />

      {/* Panel de gestion des styles de texte */}
      <TextStylesPanel isOpen={showTextStylesPanel} onClose={() => setShowTextStylesPanel(false)} />
    </aside>
  );
}

/**
 * Section des styles de texte prédéfinis
 */
const TextStylesSection: React.FC<{
  shape: ShapeData;
  onUpdate: (updates: Partial<ShapeData>) => void;
}> = ({ shape: _shape, onUpdate }) => {
  const savedStyles = useAppStore(state => state.textTool.savedStyles);

  const handleApplyStyle = useCallback(
    (style: TextStyle) => {
      onUpdate({
        fontFamily: style.fontFamily,
        fontSize: style.fontSize,
        fontStyle: style.fontWeight >= 600 ? 'bold' : 'normal',
        fill: style.color,
      });
    },
    [onUpdate]
  );

  if (savedStyles.length === 0) return null;

  return (
    <div className="control-group">
      <label className="block mt-4 mb-2">Styles prédéfinis</label>
      <div className="grid grid-cols-2 gap-1.5 max-h-[200px] overflow-y-auto">
        {savedStyles.map(style => (
          <button
            key={style.id}
            onClick={() => handleApplyStyle(style)}
            className="p-2 bg-gray-100 border border-gray-300 rounded cursor-pointer text-left transition-all duration-150 hover:bg-gray-200 hover:border-primary"
            style={{
              fontFamily: style.fontFamily,
            }}
          >
            <div
              className="mb-1"
              style={{
                fontSize: `${Math.min(style.fontSize, 16)}px`,
                fontWeight: style.fontWeight,
                color: style.color,
              }}
            >
              Aa
            </div>
            <div className="text-[11px] text-gray-600">{style.name}</div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default PropertiesPanel;
